/**
 * TelehealthSessionCard Component
 *
 * Displays a telehealth session as a card with member info,
 * status, timing, and action buttons
 */

import React from 'react';
import { formatDistanceToNow, isPast, isFuture, isToday, addMinutes } from 'date-fns';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Video,
  Phone,
  Clock,
  User,
  Calendar,
  ExternalLink,
  Play,
  XCircle,
  AlertCircle,
  Ban,
} from 'lucide-react';
import { TelehealthSessionCardProps, TelehealthSession } from './types';
import { isExternalMeetingLink } from '@/services/api/calendar';
import SessionStatusBadge, { SessionStatusDot } from './SessionStatusBadge';

export default function TelehealthSessionCard({
  session,
  onClick,
  onJoin,
  onCancel,
  showActions = true,
  compact = false,
  className = '',
}: TelehealthSessionCardProps) {
  const { fmt } = useFormattedDate();
  const scheduledTime = new Date(session.scheduled_start_time);
  const scheduledEndTime = addMinutes(scheduledTime, session.scheduled_duration_minutes || 30);
  const isSessionToday = isToday(scheduledTime);
  const isUpcoming = isFuture(scheduledTime);
  const isPastSession = isPast(scheduledTime);
  // Treat a session as "expired" only well after its slot (24h grace) — clinicians
  // routinely join late, and a demo session created off-schedule must still be
  // joinable. Blocking join 30 min after the scheduled end was the bug.
  const isExpired = isPast(addMinutes(scheduledEndTime, 24 * 60));

  // Can join in-progress (rejoin) or any scheduled/waiting session that isn't stale.
  const canJoin =
    (session.status === 'in_progress') || // Always allow rejoining in-progress sessions
    ((session.status === 'scheduled' || session.status === 'waiting') && !isExpired);
  const canCancel = session.status === 'scheduled' || session.status === 'waiting';

  // Calculate time display
  const getTimeDisplay = () => {
    if (session.status === 'in_progress') {
      return 'In progress';
    }
    if (session.status === 'waiting') {
      return 'Waiting for participant';
    }
    if (isSessionToday) {
      if (isUpcoming) {
        return `Today at ${fmt.custom(session.scheduled_start_time, 'h:mm a')}`;
      }
      return fmt.custom(session.scheduled_start_time, 'h:mm a');
    }
    if (isPastSession) {
      return fmt.custom(session.scheduled_start_time, 'MMM d, h:mm a');
    }
    return formatDistanceToNow(scheduledTime, { addSuffix: true });
  };

  // Compact view
  if (compact) {
    return (
      <div
        className={`flex items-center justify-between p-3 rounded-lg border bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors ${className}`}
        onClick={() => onClick?.(session)}
      >
        <div className="flex items-center gap-3">
          <SessionStatusDot status={session.status} />
          <div>
            <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
              {session.member_name || `Member #${session.member_id}`}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{getTimeDisplay()}</p>
          </div>
        </div>
        {showActions && (
          canJoin ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onJoin?.(session);
              }}
              className="px-3 py-1 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Join
            </button>
          ) : isExpired && (session.status === 'scheduled' || session.status === 'waiting') ? (
            <span className="px-3 py-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <Ban className="h-3 w-3" />
              Expired
            </span>
          ) : null
        )}
      </div>
    );
  }

  // Full card view
  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg border shadow-sm overflow-hidden ${className}`}
    >
      {/* Header with status */}
      <div className="px-4 py-3 border-b bg-gray-50 dark:bg-gray-850 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {session.is_audio_only ? (
            <Phone className="h-4 w-4 text-gray-500" />
          ) : (
            <Video className="h-4 w-4 text-teal-600" />
          )}
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {session.is_audio_only ? 'Audio Call' : 'Video Call'}
          </span>
        </div>
        <SessionStatusBadge status={session.status} size="sm" />
      </div>

      {/* Body */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        onClick={() => onClick?.(session)}
      >
        {/* Member info */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
            <User className="h-5 w-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              {session.member_name || `Member #${session.member_id}`}
            </h3>
            {session.event_title && (
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                {session.event_title}
              </p>
            )}
          </div>
        </div>

        {/* Time info */}
        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <span>{fmt.custom(session.scheduled_start_time, 'MMM d, yyyy')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            <span>
              {fmt.custom(session.scheduled_start_time, 'h:mm a')} ({session.scheduled_duration_minutes} min)
            </span>
          </div>
        </div>

        {/* Coordinator info */}
        {session.coordinator_name && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
            With: {session.coordinator_name}
          </p>
        )}

        {/* Outcome notes for completed sessions */}
        {session.session_outcome && session.outcome_notes && (
          <div className="mt-3 p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-600 dark:text-gray-300">
            <p className="font-medium text-xs text-gray-500 dark:text-gray-400 mb-1">
              Outcome Notes
            </p>
            <p className="line-clamp-2">{session.outcome_notes}</p>
          </div>
        )}

        {/* Follow-up indicator */}
        {session.follow_up_required && (
          <div className="mt-3 flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>
              Follow-up required
              {session.follow_up_scheduled_date &&
                ` - ${fmt.custom(session.follow_up_scheduled_date, 'MMM d')}`}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      {showActions && (canJoin || canCancel) && (
        <div className="px-4 py-3 border-t bg-gray-50 dark:bg-gray-850 flex items-center justify-end gap-2">
          {canCancel && onCancel && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCancel(session);
              }}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
          {canJoin && onJoin ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onJoin(session);
              }}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Play className="h-4 w-4" />
              {session.status === 'in_progress' ? 'Rejoin' : 'Join'}
            </button>
          ) : isExpired && (session.status === 'scheduled' || session.status === 'waiting') ? (
            <span className="flex items-center gap-1.5 px-4 py-1.5 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <Ban className="h-4 w-4" />
              Session Expired
            </span>
          ) : null}
          {/* WeaveIn video joins in-app (Join above); only a configured provider's link opens outside (TRUS-688). */}
          {isExternalMeetingLink(session.meeting_link) && session.status === 'in_progress' && (
            <a
              href={session.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
